import { useEffect } from 'react';
import { useCurrentUserEmsTeam } from '../../hooks/useCurrentUserEmsTeam';
import { useMessageHistory } from '../hooks/useMessageHistory';
import { useUsingDashboardHospitals } from '../hooks/useUsingDashboardHospitals';
import { HistoryDialog } from '../HistoryDialog';
import React from 'react';

// 定数とユーティリティのインポート
import {
  SIZE_OPTIONS,
  TEXT_COLOR_OPTIONS,
  BG_COLOR_OPTIONS,
} from './constants';

// コンポーネントのインポート
import { MessageTextarea } from './components/MessageTextarea';
import { RadioGroup } from './components/RadioGroup';
import { DateTimeInput } from './components/DateTimeInput';
import { HospitalSelector } from './components/HospitalSelector';
import { MessagePreview } from './components/MessagePreview';
import { LinkDialog } from './components/LinkDialog';

// カスタムフックのインポート
import { useFormState } from './hooks/useFormState';
import { useDateTimeHandlers } from './hooks/useDateTimeHandlers';
import { useHospitalHandlers } from './hooks/useHospitalHandlers';
import { useMessageHandlers } from './hooks/useMessageHandlers';
import { useMessageSubmit } from './hooks/useMessageSubmit';
import { useDialogHandlers } from './hooks/useDialogHandlers';

type Props = {
  /** ダイアログの開閉状態。 */
  open: boolean;
  /** ダイアログを閉じる関数。 */
  onClose: VoidFunction;
};

/** メッセージ作成ダイアログのコンポーネント。 */
export const CreateMessageDialog = ({ open, onClose }: Props) => {
  // フォーム状態管理
  const {
    messageContent,
    setMessageContent,
    selectedSize,
    setSelectedSize,
    selectedTextColor,
    setSelectedTextColor,
    selectedBgColor,
    setSelectedBgColor,
    selectedHospitals,
    setSelectedHospitals,
    displayPeriod,
    setDisplayPeriod,
    resetForm
  } = useFormState();

  // EMSチーム情報を取得
  const { emsTeam, isLoading: isEmsTeamLoading } = useCurrentUserEmsTeam();
  
  // 履歴管理フック
  const { reloadFromAPI } = useMessageHistory();
  
  // ダッシュボード使用病院リストを取得
  const { hospitals: availableHospitals, isLoading: isHospitalsLoading, error: hospitalsError, fetchHospitals } = useUsingDashboardHospitals();

  // 日付時間処理
  const { handleDateChange, handleTimeChange } = useDateTimeHandlers(displayPeriod, setDisplayPeriod);

  // 病院選択処理
  const { handleHospitalChange, handleSelectAll, handleClear } = useHospitalHandlers(
    selectedHospitals,
    setSelectedHospitals,
    availableHospitals
  );

  // メッセージ・リンク処理
  const {
    showLinkDialog,
    currentUrl,
    linkName,
    setLinkName,
    handleMessageContentChange,
    handleLinkNameSave,
    handleLinkDialogCancel
  } = useMessageHandlers(messageContent, setMessageContent);

  // メッセージ送信処理
  const { isSubmitting, handleSave } = useMessageSubmit(resetForm, reloadFromAPI);

  // ダイアログ処理
  const {
    showHistoryDialog,
    handleDialogClose,
    handleOpenHistory,
    handleCloseHistory
  } = useDialogHandlers(resetForm, onClose);

  // ダイアログが開いたときに病院リストを取得
  useEffect(() => {
    if (open && emsTeam?.emsTeamId) {
      fetchHospitals(emsTeam.emsTeamId);
    }
  }, [open, emsTeam?.emsTeamId, fetchHospitals]);

  // 病院リストの読み込み状態を統合
  const isLoading = isEmsTeamLoading || isHospitalsLoading;

  // 保存処理のラッパー
  const onSave = () => {
    handleSave(
      messageContent,
      selectedSize,
      selectedTextColor,
      selectedBgColor,
      selectedHospitals,
      displayPeriod,
      availableHospitals,
      emsTeam
    );
  };

  return open ? (
    <>
      <div className="fixed inset-0 bg-textPrimary bg-opacity-45 z-10" />
      <div className="bg-surface rounded-[28px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] max-h-[90vh] overflow-y-auto p-[32px] absolute z-20">
        <div className="flex flex-col gap-[24px]">
          <div className="flex items-center justify-between">
            <h1 className="text-[20px] text-textPrimary noto-sans-jp-bold">
              メッセージ
            </h1>
            <button
              className="w-[32px] h-[32px] flex items-center justify-center rounded-full hover:bg-gray-100 text-textSecondary hover:text-textPrimary transition-colors"
              onClick={handleDialogClose}
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 16 16" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M12 4L4 12M4 4L12 12" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* メッセージ入力 */}
          <MessageTextarea
            value={messageContent}
            onChange={handleMessageContentChange}
          />

          {/* 設定項目 */}
          <div className="flex items-center gap-[64px]">
            <RadioGroup
              label="文字サイズ"
              name="size"
              options={SIZE_OPTIONS}
              selectedValue={selectedSize}
              onChange={setSelectedSize}
            />
          </div>

          {/* 文字色選択 */}
          <div className="flex items-center gap-[64px]">
            <RadioGroup
              label="文字色"
              name="textColor"
              options={TEXT_COLOR_OPTIONS}
              selectedValue={selectedTextColor}
              onChange={setSelectedTextColor}
            />
          </div>

          {/* 背景色選択 */}
          <div className="flex items-center gap-[64px]">
            <RadioGroup
              label="背景色"
              name="bgColor"
              options={BG_COLOR_OPTIONS}
              selectedValue={selectedBgColor}
              onChange={setSelectedBgColor}
            />
          </div>

          {/* 表示期間選択 */}
          <div className="flex items-center gap-[64px]">
            <DateTimeInput
              label="表示期間"
              value={displayPeriod}
              onDateChange={handleDateChange}
              onTimeChange={handleTimeChange}
            />
          </div>

          {/* プレビュー */}
          <MessagePreview
            messageContent={messageContent}
            selectedSize={selectedSize}
            selectedTextColor={selectedTextColor}
            selectedBgColor={selectedBgColor}
          />

          {/* 表示先選択 */}
          <HospitalSelector
            availableHospitals={availableHospitals}
            selectedHospitals={selectedHospitals}
            isLoading={isLoading}
            onHospitalChange={handleHospitalChange}
            onSelectAll={handleSelectAll}
            onClear={handleClear}
          />

          {/* エラーメッセージ表示 */}
          {hospitalsError && (
            <div className="text-error text-[14px] noto-sans-jp-regular">
              {hospitalsError}
            </div>
          )}

          {/* ボタン */}
          <div className="flex justify-end gap-[16px]">
            <button
              className="px-[24px] py-[12px] rounded-[8px] text-[16px] noto-sans-jp-bold bg-gray-100 text-textPrimary hover:bg-gray-200"
              onClick={handleOpenHistory}
            >
              登録履歴参照
            </button>
            <button
              className={`px-[24px] py-[12px] rounded-[8px] text-[16px] noto-sans-jp-bold ${
                isSubmitting 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-primary text-white hover:bg-blue-700'
              }`}
              onClick={onSave}
              disabled={isSubmitting}
            >
              {isSubmitting ? '送信中...' : 'メッセージ登録'}
            </button>
          </div>
        </div>
      </div>

      {/* リンク名設定ダイアログ */}
      <LinkDialog
        open={showLinkDialog}
        currentUrl={currentUrl}
        linkName={linkName}
        onLinkNameChange={setLinkName}
        onSave={handleLinkNameSave}
        onCancel={handleLinkDialogCancel}
      />
      
      {/* 登録履歴参照ダイアログ */}
      <HistoryDialog
        open={showHistoryDialog}
        onClose={handleCloseHistory}
      />
    </>
  ) : (
    <></>
  );
}; 